import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Generation, IndicParlerApi, Reference } from './indicparler-api.service';
@Component({selector:'app-indicparler-tts', standalone:true, imports:[CommonModule,FormsModule],
  templateUrl:'./indicparler-tts.component.html',styleUrls:['./indicparler-tts.component.scss']})
export class IndicParlerTtsComponent implements OnInit, OnDestroy {
  status:any={state:'connecting'}; config:any={}; text=''; consent=false; referenceId='';
  options:Record<string,number>={}; fixedSeed=false; seed=42;
  error=''; notice=''; submitting=false; uploading=false;
  selectedFile:File|null=null; references:Reference[]=[]; history:Generation[]=[]; result:Generation|null=null;
  urls=new Map<string,string>(); referenceUrl='';
  private timer?:ReturnType<typeof setInterval>; private polling=false; private destroyed=false;
  private referenceInitialized=false;
  constructor(private api:IndicParlerApi) {}
  ngOnInit() { void this.refresh(); this.timer=setInterval(()=>void this.refresh(),3000); }
  ngOnDestroy() { this.destroyed=true; if(this.timer) clearInterval(this.timer); this.urls.forEach(u=>URL.revokeObjectURL(u)); if(this.referenceUrl) URL.revokeObjectURL(this.referenceUrl); }
  get ready() { return this.status?.modelLoaded===true; }
  get activeJobs() { return this.history.filter(j=>j.state==='queued'||j.state==='running'); }
  message(e:any,fallback:string) { return typeof e?.error?.detail==='string'?e.error.detail:fallback; }
  async refresh() {
    if(this.polling||this.destroyed) return; this.polling=true;
    try {
      this.status=await firstValueFrom(this.api.status());
      if(!this.config.model || (this.ready && !Object.keys(this.config.modelDefaults||{}).length)) {
        const first=!this.config.model; this.config=await firstValueFrom(this.api.config()); if(first) this.preset('author');
      }
      const [history,refs]=await Promise.all([firstValueFrom(this.api.history()),firstValueFrom(this.api.references())]);
      if(this.destroyed) return;
      this.history=history; this.references=refs;
      if(!this.referenceInitialized) {
        this.referenceInitialized=true;
        const preferred=refs.find(r=>r.default);
        if(preferred && !this.referenceId) { this.referenceId=preferred.referenceId; await this.previewReference(); }
      }
      if(this.result) {
        this.result=history.find(j=>j.generationId===this.result?.generationId)||this.result;
        if(this.result.audioAvailable) await this.loadAudio(this.result);
      }
    } catch(e:any) { this.status={state:'unavailable',error:this.message(e,'Unable to reach the authenticated Urdu XTTS API.')}; }
    finally { this.polling=false; }
  }
  preset(type:string) { this.options={...(type==='author'?this.config.authorPreset:this.config.modelDefaults)}; }
  chooseFile(event:Event) { this.selectedFile=(event.target as HTMLInputElement).files?.[0]||null; this.error=''; }
  async upload() {
    if(!this.selectedFile||!this.consent||this.uploading) return;
    if(this.selectedFile.size>this.config.referenceMaxBytes) { this.error='Reference exceeds the upload size limit.'; return; }
    this.uploading=true; this.error='';
    try { const ref=await firstValueFrom(this.api.upload(this.selectedFile)); this.referenceId=ref.referenceId;
      this.references=[ref,...this.references]; await this.previewReference(); this.notice='Reference uploaded. Listen to it, then generate Urdu speech.';
    } catch(e:any) { this.error=this.message(e,'Reference upload failed. Try a clean WAV, MP3 or OGG recording.'); }
    finally { this.uploading=false; }
  }
  async previewReference() {
    this.error='';
    if(this.referenceUrl) URL.revokeObjectURL(this.referenceUrl); this.referenceUrl='';
    if(!this.referenceId) return;
    try { const blob=await firstValueFrom(this.api.referenceAudio(this.referenceId));
      if(!this.destroyed) this.referenceUrl=URL.createObjectURL(blob);
    } catch(e:any) {
      this.error=e?.status===404 ? 'Reference audio was not found or has expired. Select another reference or upload a recording.'
        : this.message(e,'Reference preview could not be loaded'+(e?.status ? ' (HTTP '+e.status+')' : '')+'. Please retry; this does not mean the recording has expired.');
    }
  }
  async removeReference() {
    if(!this.referenceId||!confirm('Delete this saved reference voice recording?')) return;
    try { await firstValueFrom(this.api.deleteReference(this.referenceId)); this.references=this.references.filter(r=>r.referenceId!==this.referenceId);
      this.referenceId=''; await this.previewReference();
    } catch(e:any) { this.error=this.message(e,'Unable to delete reference.'); }
  }
  async generate() {
    if(this.submitting||!this.ready||!this.consent||!this.referenceId) return;
    this.submitting=true; this.error=''; this.notice='';
    try { this.result=await firstValueFrom(this.api.generate({text:this.text,referenceId:this.referenceId,consent:this.consent,
      generationOptions:{...this.options},seed:this.fixedSeed?this.seed:null}));
      this.notice='Request accepted. You can stay here or return to history; generation continues on the server.'; await this.refresh();
    } catch(e:any) { this.error=this.message(e,'Could not submit generation. Check history before retrying.'); }
    finally { this.submitting=false; }
  }
  async loadAudio(j:Generation) {
    if(this.urls.has(j.generationId)||!j.audioAvailable) return;
    const blob=await firstValueFrom(this.api.audio(j.generationId));
    if(!this.destroyed) this.urls.set(j.generationId,URL.createObjectURL(blob));
  }
  async inspect(j:Generation) { this.result=j; try { await this.loadAudio(j); } catch { this.error='Audio is unavailable or expired.'; } }
  reuse(j:Generation) { this.text=j.request.text; this.referenceId=this.references.some(r=>r.referenceId===j.request.referenceId)?j.request.referenceId:'';
    this.options={...j.request.generationOptions}; this.seed=j.seed; this.fixedSeed=true; void this.previewReference();
    this.notice=this.referenceId?'Input and actual settings restored.':'Settings restored. The old reference has expired or was deleted; upload or select a new one.'; }
  async remove(j:Generation) {
    if(!confirm('Delete this job, its logged input and generated audio?')) return;
    try { await firstValueFrom(this.api.delete(j.generationId)); this.history=this.history.filter(x=>x.generationId!==j.generationId);
      const url=this.urls.get(j.generationId); if(url) URL.revokeObjectURL(url); this.urls.delete(j.generationId);
      if(this.result?.generationId===j.generationId) this.result=null;
    } catch(e:any) { this.error=this.message(e,'Could not delete job.'); }
  }
  elapsed(j:Generation) { return Math.max(0,Math.round(((j.finishedAtEpoch||Date.now()/1000)-(j.startedAtEpoch||j.createdAtEpoch)))); }
}
